import React, { Reducer, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { MediaLibraryViewLayout, MediaLibraryViewProps } from './MediaLibrary.view';
import {
  BulkArchiveMediaMutationFn,
  GetMediaLibraryLazyQueryHookResult,
  MediaSelectionInput,
  MediaType
} from '../../../models/graphql';
import {
  MediaItemSelectedType,
  MediaItemUpload,
  MediaLibraryFilterAction,
  MediaLibraryFilterType
} from '../../../models/media';
import { MaxLimits, MediaUploadStatus } from '../../../constants/media';
import { AppContext } from '../../../providers/AppProvider';
import { showToast } from '../../../helpers/showToast';
import { MediaLibraryHelpers } from './mediaLibraryHelpers';
import {
  getFilterLocalStorageValue,
  MediaLibraryFiltersEnum,
  setFilterLocalStorage
} from '../../../helpers/filterStorageHelpers';
import { MediaUploadContext } from '../../../providers/MediaUploadProvider';
import { FileWithPreview } from './AddMediaSelectorModal/AddMediaSelectorModal.view';
import { useApolloClient } from '@apollo/client';
import { PostEditorContext } from '../../PostEditor/states/PostEditorProvider';
import { ExternalMedia } from '../../../hooks/useMediaUpload';

export type MediaLibraryControllerProps = {
  loadingMore: boolean;
  getMediaLibrary: GetMediaLibraryLazyQueryHookResult[0];
  mediaLibraryResults: GetMediaLibraryLazyQueryHookResult[1];
  bulkArchiveMedia: BulkArchiveMediaMutationFn;
  fetchInitialMediaItems: (filters: MediaLibraryFilterType) => void;
  fetchMoreMediaItems: () => void;
  onCloseHandler?: MediaLibraryViewProps['onCloseHandler'];
};

const filtersInitialState = (selectedWorkspaceId: string | null, asCleanFilters?: boolean): MediaLibraryFilterType => {
  const defaultFilters = {
    type: '',
    origin: '',
    proportion: '',
    tempType: '',
    tempOrigin: '',
    tempProportion: '',
    resetAll: ''
  };

  const savedFilters = {
    type: getFilterLocalStorageValue(
      selectedWorkspaceId || '',
      'MediaLibraryFilters',
      MediaLibraryFiltersEnum.mediaTypeFilter
    ),
    origin: getFilterLocalStorageValue(
      selectedWorkspaceId || '',
      'MediaLibraryFilters',
      MediaLibraryFiltersEnum.mediaOriginFilter
    ),
    proportion: getFilterLocalStorageValue(
      selectedWorkspaceId || '',
      'MediaLibraryFilters',
      MediaLibraryFiltersEnum.mediaProportionFilter
    )
  };

  return asCleanFilters
    ? defaultFilters
    : {
        ...defaultFilters,
        ...savedFilters
      };
};

export const MediaLibraryController = (props: MediaLibraryControllerProps): JSX.Element => {
  const { selectedWorkspaceId } = useContext(AppContext);
  const { initialized: isInPostEditor, dispatch: dispatchPostEditor } = useContext(PostEditorContext);
  const { uploads, queueUploads, deleteUpload } = useContext(MediaUploadContext);
  const apolloClient = useApolloClient();
  const [hasReachedFetchLimit, setHasReachedFetchLimit] = useState<boolean>(false);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [showSelectionModal, setShowSelectionModal] = useState<boolean>(isInPostEditor);
  const [showAddMediaByUrlModal, setShowAddMediaByUrlModal] = useState<boolean>(false);
  const [showFindImagesModal, setShowFindImagesModal] = useState<boolean>(false);
  const [showAddFromLibraryModal, setShowAddFromLibraryModal] = useState<boolean>(false);
  const [showDesignInCanvaModal, setDesignInCanvaModal] = useState<boolean>(false);
  const [failedUploads, setFailedUploads] = useState<Record<string, MediaItemUpload>>({});

  if (isInPostEditor) {
    // We always clear filters for Post Editor Modal of Media Library
    localStorage.setItem('PostEditorMediaLibraryFilters', '');
  }

  const [filters, dispatchFilters] = useReducer<Reducer<MediaLibraryFilterType, MediaLibraryFilterAction>>(
    MediaLibraryHelpers.mediaLibraryFilterReducer,
    filtersInitialState(selectedWorkspaceId, isInPostEditor)
  );

  const [selectedMediaItems, setSelectedMediaItems] = useState<MediaItemSelectedType[]>([]);
  const [singleActionableItemId, setSingleActionableItemId] = useState<MediaItemSelectedType[]>([]);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false);
  const [imagesIdsWithErrorSelected, setImagesIdsWithErrorSelected] = useState<MediaItemSelectedType[]>([]);

  const onUploadStatusChangeCallback = (uploadItem: MediaItemUpload, status: MediaUploadStatus) => {
    if (status === MediaUploadStatus.COMPLETED && uploadItem.completedMediaItem) {
      // TODO: potentially add this as a part of upload hook
      MediaLibraryHelpers.addItemToMediaLibraryCache(
        uploadItem.id,
        status,
        uploadItem.completedMediaItem,
        selectedWorkspaceId,
        apolloClient
      );
    } else if (status === MediaUploadStatus.FAILED) {
      setFailedUploads(old => ({ ...old, ...{ [uploadItem.id]: uploadItem } }));
    }
  };

  const fileUploadHandler = (files: FileWithPreview[] | ExternalMedia[]) => {
    const filteringActive = MediaLibraryHelpers.isFilteringActive(filters);

    // Don't try to reset if there is nothing to reset
    if (filteringActive) {
      dispatchFilters({ type: 'resetAll' });
    }

    if (!selectedWorkspaceId) {
      return;
    }

    queueUploads(files, onUploadStatusChangeCallback);

    setShowSelectionModal(false);
  };

  const { bulkArchiveMedia } = props;
  const mediaLibraryItems = useMemo(
    () => props.mediaLibraryResults.data?.workspace.mediaLibrary.items || [],
    [props.mediaLibraryResults.data?.workspace.mediaLibrary.items]
  );

  const bulkDeleteMediaHandler = useCallback(
    (selectedMedia: MediaItemSelectedType[]) => {
      const mediaToArchive = mediaLibraryItems
        .filter(i => selectedMedia.some(media => media.id === i.id && media.type === i.type))
        .map(i => ({
          type: MediaType[i.type],
          mediaId: i.id
        }));

      const uploadsToArchive = Object.values(uploads).reduce((accumulator, uploadItem) => {
        const completedItem = uploadItem.completedMediaItem;

        if (
          completedItem &&
          selectedMedia.some(media => media.id === completedItem.id && media.type === completedItem.type)
        ) {
          deleteUpload(uploadItem);
          accumulator.push({
            type: MediaType[completedItem.type],
            mediaId: completedItem.id
          });
        }
        return accumulator;
      }, [] as MediaSelectionInput[]);

      const media = [...mediaToArchive, ...uploadsToArchive];

      selectedMedia.forEach(({ id, type }) => {
        Object.values(uploads).forEach(uploadItem => {
          const completedItem = uploadItem.completedMediaItem;
          if (completedItem?.id === id && completedItem?.type === type) {
            deleteUpload(uploadItem);
          }
        });
      });

      bulkArchiveMedia({
        variables: { media },
        update: cache => {
          selectedMedia.forEach(({ id, type }) => {
            const media = mediaLibraryItems.find(i => i.id === id && i.type === type);

            cache.evict({ id: cache.identify({ id, __typename: media?.__typename }) });
            cache.gc();
          });
        }
      }).then(() => {
        // ...
      });
    },
    [bulkArchiveMedia, deleteUpload, mediaLibraryItems, uploads]
  );

  const deleteMediaHandler = useCallback(
    (selectedMedia: MediaItemSelectedType[], isBulkDelete?: boolean) => {
      if (!isBulkDelete) {
        // Keep track of single actionable item for modal
        setSingleActionableItemId(selectedMedia);

        setShowDeleteConfirmationModal(true);
      } else {
        setSingleActionableItemId([]);

        if (selectedMedia.length > 0) {
          bulkDeleteMediaHandler(selectedMedia);
        }

        const updatedSelectedMediaItems = selectedMediaItems.filter(
          media => !selectedMedia.some(({ id, type }) => media.id === id && media.type === type)
        );

        setSelectedMediaItems(updatedSelectedMediaItems);

        // Remove deleted media IDs from error-selected IDs
        setImagesIdsWithErrorSelected(prevMediaIds =>
          prevMediaIds.filter(prevMedia => !selectedMedia.some(media => media.id === prevMedia.id))
        );
      }
    },
    [bulkDeleteMediaHandler, selectedMediaItems]
  );

  const { fetchMoreMediaItems } = props;
  const fetchMoreMediaItemsHandler = useCallback(() => {
    const hasPendingUploads = Object.values(uploads).filter(i => i.status === MediaUploadStatus.PENDING).length > 0;

    // If uploads are pending, don't run this;
    // If we have reached limit, don't run this;
    if (!hasPendingUploads && !hasReachedFetchLimit) {
      fetchMoreMediaItems();
    }
  }, [hasReachedFetchLimit, fetchMoreMediaItems, uploads]);

  useEffect(() => {
    if (selectedWorkspaceId && !isInPostEditor) {
      setFilterLocalStorage(
        selectedWorkspaceId,
        'MediaLibraryFilters',
        MediaLibraryFiltersEnum.mediaTypeFilter,
        filters.type
      );
    }
  }, [filters.type, isInPostEditor, selectedWorkspaceId]);

  useEffect(() => {
    if (selectedWorkspaceId && !isInPostEditor) {
      setFilterLocalStorage(
        selectedWorkspaceId,
        'MediaLibraryFilters',
        MediaLibraryFiltersEnum.mediaOriginFilter,
        filters.origin
      );
    }
  }, [filters.origin, isInPostEditor, selectedWorkspaceId]);

  useEffect(() => {
    if (selectedWorkspaceId && !isInPostEditor) {
      setFilterLocalStorage(
        selectedWorkspaceId,
        'MediaLibraryFilters',
        MediaLibraryFiltersEnum.mediaProportionFilter,
        filters.proportion
      );
    }
  }, [filters.proportion, isInPostEditor, selectedWorkspaceId]);

  const { fetchInitialMediaItems } = props;
  useEffect(() => {
    fetchInitialMediaItems(filters);
    setSelectedMediaItems([]);
    setImagesIdsWithErrorSelected([]);
  }, [fetchInitialMediaItems, filters]);

  useEffect(() => {
    const pendingCount = Object.values(uploads).filter(i => i.status === MediaUploadStatus.PENDING).length || 0;

    // Only perform if the queue of uploads has completed; and we have failed uploads
    if (Object.keys(failedUploads).length > 0 && pendingCount === 0) {
      const isPlural = Object.keys(failedUploads).length > 1;
      const fileList = Object.values(failedUploads).map(item => {
        return item.file ? item.file.name : item.url;
      });

      // Provide a toast with the list of files
      showToast({
        summary: isPlural ? 'The following files failed to upload.' : 'The following file failed to upload.',
        detail: fileList,
        severity: 'error'
      });

      // Make sure to clear list after providing toast
      setFailedUploads({});
    }
  }, [failedUploads, uploads]); // watch for both as we need to wait for the uploads to finish

  const mediaItemsMemo = useMemo(() => {
    const mappedUploadedItems = MediaLibraryHelpers.mapUploadsToMediaItems(uploads, mediaLibraryItems, filters);

    const mappedCachedItems = MediaLibraryHelpers.mapCachedMediaToMediaItems(uploads, mediaLibraryItems);

    return [...mappedUploadedItems, ...mappedCachedItems];
  }, [uploads, mediaLibraryItems, filters]);

  useEffect(() => {
    const count = props.mediaLibraryResults.data?.workspace.mediaLibrary.count || 0;

    if (!hasReachedFetchLimit && count) {
      setHasReachedFetchLimit(() => (count / MaxLimits.QUERY) % 1 !== 0);
    }
  }, [hasReachedFetchLimit, props.mediaLibraryResults.data?.workspace.mediaLibrary]);

  const viewProps: MediaLibraryViewProps = {
    onCloseHandler: props.onCloseHandler,
    loadingMore: props.loadingMore,
    loading: props.mediaLibraryResults.loading,
    showFilterModal,
    setShowFilterModal,
    showSelectionModal,
    setShowSelectionModal,
    showAddMediaByUrlModal,
    setShowAddMediaByUrlModal,
    showFindImagesModal,
    setShowFindImagesModal,
    selectedMediaItems,
    setSelectedMediaItems,
    mediaItems: mediaItemsMemo,
    showDeleteConfirmationModal,
    setShowDeleteConfirmationModal,
    showAddFromLibraryModal,
    setShowAddFromLibraryModal,
    filters,
    dispatchFilters,
    fileUploadHandler,
    singleActionableItemId,
    deleteMediaHandler,
    fetchMoreMediaItemsHandler,
    hasReachedFetchLimit,
    isInPostEditor,
    dispatchPostEditor,
    queueUploads,
    setDesignInCanvaModal,
    showDesignInCanvaModal,
    setImagesIdsWithErrorSelected,
    imagesIdsWithErrorSelected
  };

  return <MediaLibraryViewLayout {...viewProps} />;
};
